import { Box, Flex, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { useMicLevel } from './useMicLevel';

const SEGMENTS = 12;

type MicLevelMeterProps = {
  /** Меряем только при открытом списке: захват микрофона ради невидимой полоски не нужен. */
  enabled: boolean;
  /** Выбранный микрофон. Пусто — системный по умолчанию. */
  deviceId?: string;
};

/**
 * Полоска уровня под списком микрофонов: видно, доходит ли звук от выбранного устройства.
 *
 * <p>Отвечает на «меня слышно?» до разговора, а не после него. Формулировки нарочно мягкие:
 * фоновый детектор «мёртвого микрофона» в этом же проекте пришлось убрать — он обвинял исправное
 * устройство, потому что молчащий человек и сломанный микрофон дают одинаковый ноль. Здесь молчание
 * не обвинение: человек сам открыл список и сам говорит в микрофон, поэтому пустая полоска — это
 * ответ на его собственную проверку, а не приговор оборудованию.
 */
export const MicLevelMeter = ({ enabled, deviceId }: MicLevelMeterProps) => {
  const { t } = useLingui();
  const { level, listening, heard, failed } = useMicLevel(enabled, deviceId);

  const filled = Math.round(level * SEGMENTS);

  const hint = () => {
    if (failed) {
      return t`media:level.failed`;
    }
    if (heard) {
      return t`media:level.heard`;
    }
    if (listening) {
      return t`media:level.speak`;
    }
    return t`media:level.starting`;
  };

  return (
    <Box px={2.5} pt={1} pb={1.5}>
      <Flex gap="3px" align="center" h="14px" aria-hidden="true">
        {Array.from({ length: SEGMENTS }, (_, index) => (
          <Box
            key={index}
            flex="1"
            h={index < filled ? '12px' : '4px'}
            borderRadius="full"
            bg={index < filled ? 'status.live.solid' : 'bg.emphasized'}
            transition="height 100ms ease-in-smooth, background-color 100ms ease-in-smooth"
          />
        ))}
      </Flex>

      <Text
        textStyle="xs"
        color={failed ? 'orange.400' : heard ? 'status.live.fg' : 'fg.secondary'}
        pt={1.5}
        // Роль статуса: незрячий человек проверяет микрофон тем же способом — говорит и слушает
        // ответ. Полоска ему недоступна, подпись — единственный канал.
        role="status"
      >
        {hint()}
      </Text>
    </Box>
  );
};
